import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { formatToLocal } from '@safetyhub/shared';
import type { ApproverDirectoryEntry, NotifyKind } from '@safetyhub/shared';
import {
  Badge,
  Card,
  CARD_SECTION_TITLE_CLASS,
  TABLE_HEADER_CELL_CLASS,
  TextButton,
} from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { extractApiError } from '../../lib/api';
import { approverDirectoryApi, getMemberKindBadgeTone, getMemberKindLabel, memberKindOf } from '../../lib/organizations';

/**
 * 결재 관리 — 결재자로 **실제로 쓰인** Member의 읽기 전용 조회.
 *
 * 2026-08-23 결정(approver-profile-retirement E3): 구 `ApproverProfile` CRUD를 걷어내고, 이 화면은
 * 「누가 결재자로 쓰이고 있고 통지가 닿는가」만 보여 준다. 생성·수정·삭제 축은 두지 않는다 —
 * 결재자 추가는 멤버 초대·외부 인원 등록·결재라인 편집 모달(현장 결재선 탭)에서 이미 된다.
 * 행의 각 칸은 shared `ApproverDirectoryEntry` JSDoc이 정본이고, 정렬은 서버가 한다.
 */

/** 통지 채널 표기 — 결재라인 편집 모달의 체크박스 라벨(`EditModal.tsx`)과 같은 어휘. */
const NOTIFY_KIND_LABELS: Record<NotifyKind, string> = {
  sms: 'SMS',
  email: '이메일',
};

/**
 * 도달 불가 표기. `notifyKinds`(발송 경로와 같은 판정)가 비고 **계정도 없을 때만** — phone·email 유무로
 * 화면이 다시 판정하지 않는다(계정 이메일만 있는 멤버는 연락 이메일이 없어도 이메일이 나간다).
 * 계정이 있으면(`hasAccount`) 외부 채널이 비어도 인앱 알림·결재함으로 닿으므로 경고가 아니다(W-h).
 */
const UNREACHABLE_LABEL = '연락처 없음 — 통지 불가';
/** 외부 채널(SMS·이메일) 없이 계정만 있는 멤버 — 인앱으로만 닿는다. */
const IN_APP_ONLY_LABEL = '인앱만';

/** 현장 결재선 탭 직링크(`SiteDetail/types.ts` `parseSiteDetailTab`이 읽는 쿼리). */
function siteApprovalLineHref(siteId: string): string {
  return `/sites/${siteId}?tab=approval`;
}

function formatSignedAt(iso: string | null): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return formatToLocal(date, {
    format: { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' },
  });
}

export default function ApprovalSettings() {
  const { currentMembership } = useAuthStore();
  const companyId = currentMembership?.companyId;
  const deviceLayout = useDeviceLayout();
  const isMobile = deviceLayout === 'phone' || deviceLayout === 'fold';

  const [entries, setEntries] = useState<ApproverDirectoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  /**
   * 목록 로드. `isCancelled`는 조직 전환(companyId 변경) 뒤 도착한 **이전 조직**의 응답을 버리는
   * 가드다 — 느린 A 응답이 빠른 B 응답을 덮어 B 화면에 A 결재자가 그려지는 stale 반영 방지(W-h).
   */
  const loadData = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      if (!companyId) return;
      setIsLoading(true);
      setError('');
      try {
        const list = await approverDirectoryApi.list(companyId);
        if (isCancelled()) return;
        setEntries(list);
      } catch (err) {
        if (isCancelled()) return;
        const apiError = extractApiError(err);
        setError(apiError.message);
      } finally {
        if (!isCancelled()) setIsLoading(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    loadData(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [companyId, loadData]);

  if (!companyId) {
    return (
      <div className="text-center py-12">
        <p className="text-ink-500">조직을 선택해주세요.</p>
      </div>
    );
  }

  return (
    <div className={isMobile ? 'space-y-4' : 'space-y-6'}>
      {/* 헤더 — 생성 버튼 없음(읽기 전용 화면) */}
      {isMobile ? (
        <h1 className="text-lg font-bold tracking-tight text-ink-900">결재 관리</h1>
      ) : (
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">결재 관리</h1>
          <p className="mt-1 text-[15px] font-medium text-ink-500">
            결재자로 지정되거나 결재한 멤버와 통지 가능 여부를 확인합니다.
          </p>
        </div>
      )}

      {/* 현장별 결재선 안내 */}
      <div className="rounded-card bg-brand-50 border border-brand-200 p-4 flex items-start gap-3">
        <svg className="h-5 w-5 text-brand-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p className="text-sm text-brand-800 font-medium">현장별 결재선은 각 현장 상세 페이지의 '결재선' 탭에서 관리합니다.</p>
          <p className="text-xs text-brand-700 mt-1">
            결재자 추가는 멤버 초대, 외부 인원 등록, 결재선 편집에서 합니다. 아래 표의 현장을 누르면 그 현장의
            결재선 탭으로 이동합니다.
          </p>
          <Link
            to="/sites"
            className="mt-2 inline-flex items-center text-sm text-brand-600 hover:text-brand-800 font-medium"
          >
            현장 목록으로 이동
            <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>

      {error && (
        <div className="rounded-card bg-rose-50 p-4">
          <p className="text-sm text-rose-700">{error}</p>
          <TextButton tone="danger" onClick={() => setError('')} className="mt-2 text-sm">
            닫기
          </TextButton>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : (
        <Card padding="xl" radius="lg" elevation="md">
          <h2 className={`${CARD_SECTION_TITLE_CLASS} mb-4`}>결재자</h2>

          {entries.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-ink-500">아직 결재자로 지정되거나 결재한 멤버가 없습니다.</p>
              <p className="text-xs text-ink-400 mt-1">
                현장 상세의 '결재선' 탭에서 결재선을 설정하면 여기에 나타납니다.
              </p>
            </div>
          ) : isMobile ? (
            <ul className="space-y-3">
              {entries.map((entry) => (
                <li key={entry.memberId} className="rounded-card border border-line-soft p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <NameCell entry={entry} />
                    <RoleCell entry={entry} />
                  </div>
                  <div className="text-sm text-ink-500">
                    <ContactCell contact={entry.contact} />
                  </div>
                  <NotifyKindsCell kinds={entry.notifyKinds} hasAccount={entry.hasAccount} />
                  <SiteLinesCell siteLines={entry.siteLines} />
                  <div className="text-xs text-ink-500">
                    <ApprovalsCell approvals={entry.approvals} />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className={TABLE_HEADER_CELL_CLASS}>이름</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>역할</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>연락처</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>통지 가능 채널</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>지정된 현장 결재선</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>결재 이력</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-line-soft">
                  {entries.map((entry) => (
                    <tr key={entry.memberId}>
                      <td className="px-4 py-3 text-sm font-medium text-ink-900">
                        <NameCell entry={entry} />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <RoleCell entry={entry} />
                      </td>
                      <td className="px-4 py-3 text-sm text-ink-500">
                        <ContactCell contact={entry.contact} />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <NotifyKindsCell kinds={entry.notifyKinds} hasAccount={entry.hasAccount} />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <SiteLinesCell siteLines={entry.siteLines} />
                      </td>
                      <td className="px-4 py-3 text-sm text-ink-500 whitespace-nowrap">
                        <ApprovalsCell approvals={entry.approvals} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/**
 * 이름 + 소속 배지. 축은 `membership`(이 회사 CompanyMember 행) 하나다 —
 * `none`(행 없음) = 연락처만 등록한 외부 인원 「외부」 / `left`(leftAt 있음) = 「퇴사」(결재 이력이
 * 남아 목록에 오르지만 지금은 소속이 아니다 — 외부 인원과 다른 상태라 같은 배지를 쓰지 않는다).
 */
function NameCell({ entry }: { entry: ApproverDirectoryEntry }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="font-medium text-ink-900">{entry.name}</span>
      {entry.membership === 'none' && (
        <Badge tone="amber" shape="rounded" size="sm">
          외부
        </Badge>
      )}
      {entry.membership === 'left' && (
        <Badge tone="slate" shape="rounded" size="sm">
          퇴사
        </Badge>
      )}
    </span>
  );
}

/**
 * 종류(소유자/구성원)는 **활성** 멤버십의 것(`isOwner`·`countsAsMember`, P7-b-2 — 옛 `role`) — 외부 인원(`membership: 'none'`)도
 * 퇴사자(`'left'`)도 false라 칸을 비운다. 둘의 구분은 종류가 아니라 `NameCell`의 배지 축(membership)이 맡는다.
 */
function RoleCell({ entry }: { entry: ApproverDirectoryEntry }) {
  const kind = entry.membership === 'active' ? memberKindOf(entry) : null;
  if (kind === null) return <span className="text-ink-400">-</span>;
  return (
    <Badge tone={getMemberKindBadgeTone(kind)} shape="rounded">
      {getMemberKindLabel(kind)}
    </Badge>
  );
}

/**
 * 연락처 — 발송 경로가 쓰는 값 그대로. 연락 이메일이 없고 계정 이메일만 있으면 「계정 이메일」로
 * 구분해 적는다(그 칸이 실제 발송처라는 사실은 shared JSDoc 참조).
 */
function ContactCell({ contact }: { contact: ApproverDirectoryEntry['contact'] }) {
  const lines: string[] = [];
  if (contact.phone) lines.push(contact.phone);
  if (contact.email) lines.push(contact.email);
  else if (contact.accountEmail) lines.push(`${contact.accountEmail} (계정 이메일)`);
  if (lines.length === 0) return <span className="text-ink-400">-</span>;
  return (
    <span className="flex flex-col gap-0.5">
      {lines.map((line, index) => (
        // 같은 문자열이 phone·email 양쪽에 올 수 있어(중복 key) 위치를 key로 쓴다 — 정적 목록이라 안전.
        <span key={index}>{line}</span>
      ))}
    </span>
  );
}

/**
 * 통지 가능 채널. 외부 채널(`notifyKinds`)이 비어 있어도 계정이 있으면(`hasAccount`) 인앱으로
 * 닿는다 — 그 경우는 중립 「인앱만」이고, **둘 다** 없을 때만 경고 「연락처 없음 — 통지 불가」.
 */
function NotifyKindsCell({ kinds, hasAccount }: { kinds: NotifyKind[]; hasAccount: boolean }) {
  if (kinds.length === 0) {
    if (hasAccount) {
      return (
        <Badge tone="slate" shape="rounded" size="sm">
          {IN_APP_ONLY_LABEL}
        </Badge>
      );
    }
    return (
      <Badge tone="amber" shape="rounded" size="sm">
        {UNREACHABLE_LABEL}
      </Badge>
    );
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      {kinds.map((kind) => (
        <Badge key={kind} tone="emerald" shape="rounded" size="sm">
          {NOTIFY_KIND_LABELS[kind]}
        </Badge>
      ))}
    </span>
  );
}

function SiteLinesCell({ siteLines }: { siteLines: ApproverDirectoryEntry['siteLines'] }) {
  if (siteLines.length === 0) return <span className="text-ink-400">-</span>;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {siteLines.map((site) => (
        <Link
          key={site.siteId}
          to={siteApprovalLineHref(site.siteId)}
          className="inline-flex items-center rounded-card-sm bg-brand-50 px-2 py-[3px] text-[11.5px] font-bold text-brand-700 hover:bg-brand-100"
          title={`${site.siteName} 결재선 탭으로 이동`}
        >
          {site.siteName}
        </Link>
      ))}
    </span>
  );
}

function ApprovalsCell({ approvals }: { approvals: ApproverDirectoryEntry['approvals'] }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span>
        총 {approvals.total}건 · 대기 {approvals.pending}
      </span>
      <span className="text-xs text-ink-400">마지막 서명 {formatSignedAt(approvals.lastSignedAt)}</span>
    </span>
  );
}
